// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {type InputActionMeta} from 'react-select';

import {
  type ArtifactPathSearchArtifactsQuery,
  useArtifactPathSearchArtifactsQuery,
} from '../../../../../generated/graphql';
import {type ArtifactOption, getArtifactOptions} from '../util';
import * as S from './ArtifactPathSelector.styles';

interface ArtifactSelectorProps {
  entityName?: string;
  projectName?: string;
  value?: ArtifactOption | null;
  onArtifactSelect?: (artifact: ArtifactOption | null) => void;
  onArtifactOptionsLoaded?: (options: ArtifactOption[]) => void;
  initialSearchString?: string;
}

export const ArtifactSelector = ({
  entityName,
  projectName,
  value,
  onArtifactSelect,
  onArtifactOptionsLoaded,
  initialSearchString = '',
}: ArtifactSelectorProps) => {
  const [artifactSearchString, setArtifactSearchString] =
    useState(initialSearchString);

  const {
    data: artifactData,
    loading: artifactsLoading,
    error: artifactsError,
  } = useArtifactPathSearchArtifactsQuery({
    variables: {
      entityName: entityName || '',
      projectName: projectName || '',
      limit: 50,
      searchTerm: artifactSearchString,
      artifactsLimit: 10,
    },
    skip: !entityName || !projectName,
  });

  const artifactOptions = getArtifactOptions(
    artifactData ?? ({} as ArtifactPathSearchArtifactsQuery)
  );

  // prevent dropdown from closing during search by waiting for initial load
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    // previous artifact search is no longer relevant for the new project
    setArtifactSearchString('');
    setInitialLoadComplete(false);
  }, [entityName, projectName]);

  useEffect(() => {
    if (onArtifactOptionsLoaded && artifactOptions.length > 0) {
      onArtifactOptionsLoaded(artifactOptions);
    }
  }, [artifactOptions, onArtifactOptionsLoaded]);

  useEffect(() => {
    // avoid condition where query is skipped/cached
    // and loading never becomes false
    const isQuerySkipped = !entityName || !projectName;
    if (isQuerySkipped || !artifactsLoading) {
      setInitialLoadComplete(true);
    }
  }, [artifactsLoading, entityName, projectName]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchString: string) => {
        setArtifactSearchString(searchString);
      }, 300),
    []
  );

  const onInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        debouncedSearch(newValue);
      }
    },
    [debouncedSearch]
  );

  const handleChange = useCallback(
    (option: ArtifactOption | null) => {
      if (onArtifactSelect) {
        onArtifactSelect(option);
      }
    },
    [onArtifactSelect]
  );

  const isDisabled = !entityName || !projectName;

  // don't show loading when there's nothing to load (no project selected)
  if (!isDisabled && !initialLoadComplete) {
    return <Select isDisabled placeholder="Loading artifacts..." />;
  }

  const select = (
    <S.DisabledDropdownContainer>
      <Select
        value={value}
        options={artifactOptions}
        placeholder="Select artifact"
        getOptionValue={option => option.value}
        getOptionLabel={option => option.label}
        onInputChange={onInputChange}
        onChange={handleChange}
        isSearchable
        isClearable={true}
        isDisabled={isDisabled}
      />
    </S.DisabledDropdownContainer>
  );

  if (isDisabled) {
    return (
      <Tooltip
        side="top"
        align="start"
        trigger={select}
        content="Select a project first"
      />
    );
  }

  return select;
};
